import { useEffect, useState } from "react";
import moment from "moment";
import { IconButton } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "@/utils/appToast";
import ContentContainer from "@/components/containers/contentContainer";
import NewProposalForm from "@/pages/hire/new-proposal-form";
import { Spinner } from "@/shared/spinner/spinner";
import { api } from "@/utils/api";
import { getBackendCallErrorMessage } from "@/utils/backendErrorMessage";
import { ChatBlackIcon } from "@/components/assets/icons/svgs";

const getServiceName = (job: any, lang?: string) => {
  const service = job?.service;
  if (!service) return "-";
  if (typeof service === "string") return service;
  return (
    service?.mlname?.[lang || "en"] ||
    service?.mlname?.en ||
    service?.name ||
    "-"
  );
};

const getClientName = (job: any) => {
  const client = job?.user || job?.client;
  if (!client || typeof client !== "object") return "-";
  const name = [client.first_name, client.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || "-";
};

const unwrapDetail = (res: any) => {
  if (!res) return null;
  const body = res.success !== undefined ? res.data : res;
  if (!body || typeof body !== "object") return null;
  if (body.requestQuoteJob) return body;
  if (body.data?.requestQuoteJob) return body.data;
  return body;
};

export default function ProviderRequestQuoteDetailPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { requestQuoteJobId } = useParams<{ requestQuoteJobId: string }>();
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<any>(null);

  const loadDetail = async () => {
    if (!requestQuoteJobId) return;
    setLoading(true);
    try {
      const res = await api.getProviderRequestQuoteInboxDetail(requestQuoteJobId);
      if (res?.success === false) {
        toast.error(
          getBackendCallErrorMessage(res) ||
            res?.message ||
            "Failed to load request quote"
        );
        setDetail(null);
        return;
      }
      setDetail(unwrapDetail(res));
    } catch (err: any) {
      toast.error(err?.message || "Failed to load request quote");
      setDetail(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestQuoteJobId]);

  const job = detail?.requestQuoteJob;
  const jobMongoId = job?._id || requestQuoteJobId;
  const canEdit = detail?.canEditProposal !== false && !detail?.isConverted;
  const proposalStatus = detail?.proposalStatus || "awaiting";
  const isCreateProposal =
    String(proposalStatus).toLowerCase() === "awaiting" || !detail?.myProposal;

  if (loading) {
    return (
      <ContentContainer className="!px-0 m-0 bg-[#f9f9ff] min-h-screen">
        <div className="flex justify-center py-16">
          <Spinner isLoading size="medium" />
        </div>
      </ContentContainer>
    );
  }

  if (!detail || !jobMongoId) {
    return (
      <ContentContainer className="!px-0 m-0 bg-[#f9f9ff] min-h-screen">
        <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-6 py-10 flex flex-col gap-4">
          <p className="text-sm text-[#424654]">
            {t("requestQuoteJobNotFound") ||
              "Unable to load this quote request."}
          </p>
          <button
            type="button"
            onClick={() => navigate("/provider-request-quotes")}
            className="w-fit h-10 px-5 rounded-lg border border-[#d8e3fb] bg-white text-sm font-medium text-[#4285F4]"
          >
            ← {t("back") || "Back"}
          </button>
        </div>
      </ContentContainer>
    );
  }

  return (
    <ContentContainer className="!px-0 m-0 bg-[#f9f9ff] min-h-screen pb-8">
      <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-4 h-14 bg-[#f9f9ff]/80 backdrop-blur-md shadow-sm">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            className="size-11 shrink-0 rounded-full flex items-center justify-center text-[#111c2d] text-lg"
            onClick={() => navigate("/provider-request-quotes")}
            aria-label={t("back")}
          >
            ←
          </button>
          <h1 className="text-xl font-semibold text-[#111c2d] truncate">
            {t("providerRequestQuoteDetail") || "Quote request"}
          </h1>
        </div>
        {detail.chatId && (
          <IconButton
            size="small"
            onClick={() =>
              navigate(`/chat/${detail.chatId}`, {
                state: {
                  returnTo: `/provider-request-quotes/${requestQuoteJobId}`,
                },
              })
            }
            aria-label={t("openChat") || "Open chat"}
            title={t("openChat") || "Open chat"}
          >
            <ChatBlackIcon />
          </IconButton>
        )}
      </div>

      <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-6 pt-6 flex flex-col gap-6 min-w-0">
        {(detail.isConverted || proposalStatus === "converted") && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {t("quoteConvertedToJob") ||
              "This quote request was accepted and converted into a job."}
          </div>
        )}

        <div className="bg-[#e7eeff] rounded-xl p-4 shadow-sm flex flex-col gap-4 min-w-0 overflow-hidden">
          <div className="flex items-center gap-3">
            <div className="size-8 shrink-0 rounded-full bg-[#4285F4]/10 flex items-center justify-center text-[#4285F4] text-sm font-bold">
              ✓
            </div>
            <h2 className="text-xl font-semibold text-[#111c2d]">
              {t("quoteDetails") || "Quote details"}
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 min-w-0">
            <div className="min-w-0">
              <p className="text-xs font-medium text-[#424654]">
                {t("jobId")}
              </p>
              <p
                className="text-sm font-semibold text-[#111c2d] mt-0.5 truncate"
                title={job?.uid || jobMongoId}
              >
                {job?.uid || jobMongoId}
              </p>
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-[#424654]">
                {t("service_name")}
              </p>
              <p
                className="text-sm text-[#111c2d] mt-0.5 truncate"
                title={getServiceName(job, i18n.language)}
              >
                {getServiceName(job, i18n.language)}
              </p>
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-[#424654]">
                {t("client") || "Client"}
              </p>
              <p
                className="text-sm text-[#111c2d] mt-0.5 truncate"
                title={getClientName(job)}
              >
                {getClientName(job)}
              </p>
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-[#424654]">
                {t("status")}
              </p>
              <p className="text-sm text-[#111c2d] mt-0.5 capitalize">
                {t(`proposalStatus_${proposalStatus}`) || proposalStatus}
              </p>
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-[#424654]">
                {t("requestedDate") || "Requested date"}
              </p>
              <p className="text-sm text-[#111c2d] mt-0.5">
                {detail?.sentAt || job?.createdAt
                  ? moment(detail?.sentAt || job?.createdAt).format(
                      "DD MMM YYYY"
                    )
                  : "-"}
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3 pt-1 border-t border-[#d8e3fb]/60 min-w-0">
            <div className="min-w-0">
              <p className="text-xs font-medium text-[#424654]">
                {t("urgency")}
              </p>
              <p className="text-sm text-[#111c2d] mt-0.5 capitalize">
                {job?.urgency || "-"}
              </p>
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-[#424654]">
                {t("select_start_date_time")}
              </p>
              <p className="text-sm text-[#111c2d] mt-0.5">
                {job?.schedule?.startDateTime
                  ? moment(job.schedule.startDateTime).format(
                      "DD MMM YYYY, h:mm A"
                    )
                  : "-"}
              </p>
            </div>
          </div>

          {job?.location?.value && (
            <div className="min-w-0 pt-1 border-t border-[#d8e3fb]/60">
              <p className="text-xs font-medium text-[#424654]">
                {t("serviceLocation")}
              </p>
              <p className="text-sm text-[#111c2d] mt-1 break-words [overflow-wrap:anywhere] max-w-full">
                {job.location.value}
              </p>
            </div>
          )}
          {job?.description && (
            <div className="min-w-0 pt-1 border-t border-[#d8e3fb]/60">
              <p className="text-xs font-medium text-[#424654]">
                {t("description")}
              </p>
              <p className="text-sm text-[#111c2d] mt-1 break-words whitespace-pre-wrap [overflow-wrap:anywhere] max-w-full">
                {job.description}
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3">
          <div className="rounded-xl border border-[#d8e3fb] bg-white shadow-sm overflow-hidden">
            {canEdit || detail.myProposal ? (
              <NewProposalForm
                requestQuoteJobId={String(jobMongoId)}
                proposalMode={isCreateProposal ? "create" : "edit"}
                onSuccess={() => {
                  navigate("/provider-request-quotes");
                }}
                onClose={() => navigate("/provider-request-quotes")}
              />
            ) : (
              <div className="p-6 text-sm text-[#424654]">
                {t("cannotEditProposalForQuote") ||
                  "You can no longer edit a proposal for this quote."}
              </div>
            )}
          </div>
        </div>
      </div>
    </ContentContainer>
  );
}
